import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, MapPin, Clock, Building2, Mail, ArrowLeft, Sparkles } from "lucide-react";
import Seo, { breadcrumbLd, setJsonLd } from "@/components/Seo";
import Navbar from "@/components/tchatix/Navbar";
import Footer from "@/components/tchatix/Footer";
import { useTranslation } from "react-i18next";
import { JOBS, getJobBySlug, jobPostingLd } from "@/data/careers";

export default function RecrutementPoste() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const job = getJobBySlug(slug);

  // Injecte le JobPosting JSON-LD (une seule annonce = une URL dédiée).
  useEffect(() => {
    if (!job) return;
    const path = `/recrutement/${job.slug}`;
    setJsonLd("job-posting", jobPostingLd(job, path));
    return () => setJsonLd("job-posting", null);
  }, [slug]);

  if (!job) {
    return (
      <div className="relative min-h-screen text-[#001D31] overflow-x-hidden">
        <Seo title={t("careers.notFoundTitle")} description={t("careers.metaDesc")} path="/recrutement" />
        <Navbar />
        <section className="pt-36 pb-24 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-3xl font-semibold mb-4 [font-family:'Playfair_Display',_serif]">
              {t("careers.notFoundTitle")}
            </h1>
            <p className="text-[#8896B0] mb-8">{t("careers.notFoundText")}</p>
            <Link to="/recrutement" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-medium ring-1 ring-[#FCE300]/30"
              style={{ background: "linear-gradient(120deg, #001D31 0%, #0A3A5C 60%, #001D31 100%)" }}>
              <ArrowLeft className="w-4 h-4" /> {t("careers.backToList")}
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const path = `/recrutement/${job.slug}`;

  return (
    <div className="relative min-h-screen text-[#001D31] overflow-x-hidden">
      <Seo
        title={`${job.title} — Tchatix`}
        description={job.excerpt}
        path={path}
        type="article"
        jsonLd={breadcrumbLd([
          { name: t("careers.breadcrumbHome"), path: "/" },
          { name: t("careers.breadcrumbCareers"), path: "/recrutement" },
          { name: job.title, path },
        ])}
      />
      <Navbar />

      {/* ── Hero du poste ── */}
      <section className="relative pt-36 pb-12 px-6 overflow-hidden tx-grid-bg">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#0985D8] opacity-[0.06] blur-[130px] pointer-events-none" />
        <div className="relative max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Link to="/recrutement" className="flex items-center gap-1.5 text-sm text-[#0985D8] hover:text-[#001D31] mb-5 w-fit">
              <ArrowLeft className="w-4 h-4" /> {t("careers.back")}
            </Link>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full tx-glass-strong text-sm text-[#0985D8] mb-6">
              <Sparkles className="w-3.5 h-3.5 text-[#FF5A3C]" /> {t("careers.badge")}
            </span>
            <h1 className="text-3xl md:text-4xl font-semibold leading-tight mb-5 [font-family:'Playfair_Display',_serif]">
              {job.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-[#8896B0] mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full tx-glass">
                <MapPin className="w-4 h-4 text-[#0985D8]" /> {job.location}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full tx-glass">
                <Clock className="w-4 h-4 text-[#0985D8]" /> {job.contract} · {job.type}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full tx-glass">
                <Building2 className="w-4 h-4 text-[#0985D8]" /> {job.remote}
              </span>
            </div>
            <p className="text-[#8896B0] leading-relaxed text-lg">{job.excerpt}</p>
          </motion.div>
        </div>
      </section>

      {/* ── Détail du poste ── */}
      <section className="px-6 pb-24">
        <div className="max-w-3xl mx-auto space-y-8">
          {/* Intro / présentation de l'offre */}
          {(job.intro || []).length > 0 && (
            <div className="space-y-3 text-[#001D31] leading-relaxed">
              {job.intro.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          )}

          {/* Sections structurées de l'offre */}
          {(job.sections || []).map((s, si) => (
            <div key={si}>
              <h2 className="text-xl font-semibold mb-4 [font-family:'Playfair_Display',_serif]">
                {s.title}
              </h2>
              <div className="space-y-3">
                {s.blocks.map((b, bi) =>
                  b.t === "ul" ? (
                    <ul key={bi} className="space-y-2">
                      {b.c.map((x, i) => (
                        <li key={i} className="text-[#001D31] flex gap-2 leading-relaxed">
                          <span className="text-[#0985D8] mt-1 shrink-0">→</span> {x}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p key={bi} className="text-[#001D31] leading-relaxed">{b.c}</p>
                  )
                )}
              </div>
            </div>
          ))}

          {/* CTA Postuler */}
          <div className="tx-glass rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#0985D8]/15 text-[#0985D8] flex items-center justify-center shrink-0">
                <Briefcase className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#001D31]">{t("careers.applyTitle")}</p>
                <p className="text-xs text-[#8896B0]">{t("careers.applySub")}</p>
              </div>
            </div>
            <a
              href={`mailto:contact@tchatix.com?subject=Candidature — ${encodeURIComponent(job.title)}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-medium ring-1 ring-[#FCE300]/30 transition-transform hover:scale-[1.02] shrink-0"
              style={{ background: "linear-gradient(120deg, #001D31 0%, #0A3A5C 60%, #001D31 100%)" }}
            >
              <Mail className="w-4 h-4" /> {t("careers.apply")}
            </a>
          </div>

          {/* Autres postes */}
          <div className="pt-4 border-t border-[#001D31]/10">
            <p className="text-xs uppercase tracking-widest text-[#0985D8] font-semibold mb-4">
              {t("careers.otherOpenings")}
            </p>
            <div className="space-y-3">
              {JOBS.filter((j) => j.slug !== job.slug).map((j) => (
                <Link
                  key={j.slug}
                  to={`/recrutement/${j.slug}`}
                  className="block tx-glass rounded-xl p-4 hover:border-[#0985D8]/40 transition-colors"
                >
                  <p className="text-sm font-semibold text-[#001D31] [font-family:'Playfair_Display',_serif]">
                    {j.title}
                  </p>
                  <p className="text-xs text-[#8896B0] mt-0.5 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> {j.location} · {j.contract}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}